import { RotateCcw } from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";

import type { HistoryEntry } from "@/components/HistoryDrawer";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Modal } from "@/components/ui/modal";
import { SearchableSelect } from "@/components/ui/searchable-select";
import { toast } from "@/components/ui/sonner";

import { checkRestore, useRestore, type RestoreCheck } from "../api";

/**
 * Undo a catalogue deletion from the recent history, as the Django drawer's
 * button does: the server says what the restore needs; nothing missing
 * restores at once, a mandatory parent that is gone asks for a stand-in (or a
 * cancel, to restore that parent first), and links that are gone ask for a
 * go-ahead.
 */
export function RestoreButton({ entry }: { entry: HistoryEntry }) {
  const { t } = useTranslation();
  const restore = useRestore();
  const [asking, setAsking] = useState<RestoreCheck | null>(null);
  const [busy, setBusy] = useState(false);
  const name = entry.item?.name ?? "";
  const logId = entry.restore_id as string;

  const refused = (code: string) =>
    toast.error(
      code === "restored" ? t("catalogue.restore.alreadyRestored") : t("catalogue.restore.error"),
    );

  const start = async () => {
    setBusy(true);
    try {
      const checked = await checkRestore(logId);
      if (checked.status === "refused") return refused(checked.code);
      if (checked.status !== "ask") return;
      const { required, warnings } = checked.check;
      if (required.length || warnings.length) return setAsking(checked.check);
      const done = await restore.mutateAsync({ logId });
      if (done.status === "restored") toast.success(t("catalogue.restore.done", { name }));
      else if (done.status === "ask") setAsking(done.check);
      else refused(done.code);
    } catch {
      refused("error");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="mt-2 flex justify-end">
      <Button variant="outline" size="sm" onClick={() => void start()} disabled={busy}>
        <RotateCcw aria-hidden className="size-4" />
        {t("catalogue.restore.button")}
      </Button>
      {asking && (
        <RestoreDialog
          logId={logId}
          name={name}
          check={asking}
          onClose={() => setAsking(null)}
        />
      )}
    </div>
  );
}

function RestoreDialog({
  logId,
  name,
  check: initial,
  onClose,
}: {
  logId: string;
  name: string;
  check: RestoreCheck;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const restore = useRestore();
  const [check, setCheck] = useState(initial);
  const [choices, setChoices] = useState<Record<string, string>>(() =>
    Object.fromEntries(initial.required.map((need) => [need.field, need.chosen])),
  );
  const [failed, setFailed] = useState<string | null>(null);
  const ready = check.required.every((need) => choices[need.field]);

  const confirm = async () => {
    setFailed(null);
    try {
      // Only warnings the person has seen are accepted.
      const done = await restore.mutateAsync({
        logId,
        choices,
        acceptWarnings: check.warnings.length > 0,
      });
      if (done.status === "restored") {
        toast.success(t("catalogue.restore.done", { name }));
        onClose();
      } else if (done.status === "ask") {
        setCheck(done.check);
      } else {
        setFailed(
          done.code === "restored"
            ? t("catalogue.restore.alreadyRestored")
            : t("catalogue.restore.error"),
        );
      }
    } catch {
      setFailed(t("catalogue.restore.error"));
    }
  };

  return (
    <Modal title={t("catalogue.restore.title", { name })} onClose={onClose} className="max-w-lg">
      <div className="space-y-3 text-sm">
        {check.required.map((need) => (
          <div
            key={need.field}
            role="alert"
            className="space-y-2 rounded-md border border-destructive/30 bg-destructive/10 p-3"
          >
            <p className="text-destructive-ink">
              {need.missing
                ? t("catalogue.restore.required", { field: need.label, name: need.missing })
                : t("catalogue.restore.requiredUnnamed", { field: need.label })}
            </p>
            <Label htmlFor={`restore-${need.field}`} className="block">
              {need.label}
            </Label>
            <SearchableSelect
              id={`restore-${need.field}`}
              value={choices[need.field] ?? ""}
              onChange={(value) => setChoices((current) => ({ ...current, [need.field]: value }))}
              placeholder={t("catalogue.restore.choose")}
              searchPlaceholder={t("catalogue.searchPlaceholder")}
              emptyLabel={t("catalogue.noResults")}
              options={need.options.map((option) => ({
                id: option.id,
                name: option.crumb ? `${option.name} — ${option.crumb}` : option.name,
              }))}
            />
          </div>
        ))}
        {check.warnings.length > 0 && (
          <div className="space-y-2 rounded-md border border-warning/40 bg-warning/10 p-3">
            <p>{t("catalogue.restore.warnings")}</p>
            <ul className="list-disc space-y-1 pl-5">
              {check.warnings.map((warning) => (
                <li key={warning.label}>
                  {warning.count === null ? warning.label : `${warning.label} (${warning.count})`}
                </li>
              ))}
            </ul>
            <p>{t("catalogue.restore.continue")}</p>
          </div>
        )}
        {failed && (
          <p role="alert" className="text-destructive-ink">
            {failed}
          </p>
        )}
      </div>
      <div className="mt-4 flex justify-end gap-2 border-t border-border pt-4">
        <Button variant="outline" onClick={onClose}>
          {t("catalogue.cancel")}
        </Button>
        <Button onClick={() => void confirm()} disabled={!ready || restore.isPending}>
          {t("catalogue.restore.button")}
        </Button>
      </div>
    </Modal>
  );
}
